/**
 * Dictionary-based i18n.
 *
 * Design choices:
 *   - Both dictionaries ship in the bundle. They're small (< 10KB each) and
 *     inlining them avoids a late fetch + "flash of English" on language switch.
 *   - Strictly typed `Dictionary` shape — adding a key in pt-BR without the
 *     same key in en.ts is a compile error.
 *   - Format helpers (BRL, dates, relative time) live here because locale
 *     controls them as much as it does strings.
 *   - Timezone is pinned to America/Sao_Paulo. Even an English-speaking user
 *     on Builder is viewing a Brazilian construction site's schedule; local
 *     project time > device time.
 */

import { ptBR } from './pt-BR';
import { en } from './en';
import type { Dictionary, LocaleCode, LocaleDescriptor } from './types';

const DICTIONARIES: Record<LocaleCode, Dictionary> = {
  'pt-BR': ptBR,
  en,
};

export const AVAILABLE_LOCALES: LocaleDescriptor[] = [
  { code: 'pt-BR', label: 'Português', flag: '🇧🇷' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
];

export const PROJECT_TIMEZONE = 'America/Sao_Paulo';

const STORAGE_KEY = 'builder:locale';

function readStoredLocale(): LocaleCode {
  if (typeof window === 'undefined') return 'pt-BR';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'pt-BR' || raw === 'en') return raw;
  } catch {
    // Ignore.
  }
  return 'pt-BR';
}

let current: LocaleCode = readStoredLocale();
const subscribers = new Set<() => void>();

export function initLocale() {
  // Deep-link override: `?lang=en` / `?lang=pt-BR` wins once and persists,
  // so a shared link can open the app in a specific language.
  try {
    if (typeof window !== 'undefined') {
      const qp = new URLSearchParams(window.location.search).get('lang');
      if (qp === 'pt-BR' || qp === 'en') {
        current = qp;
        window.localStorage.setItem(STORAGE_KEY, qp);
      }
    }
  } catch {
    // Ignore.
  }
  // Reflect current locale on <html lang>. Helps assistive tech and the
  // browser's built-in hyphenation/spellcheck pick the right dictionary.
  if (typeof document !== 'undefined') {
    document.documentElement.lang = current;
  }
}

export function getLocale(): LocaleCode {
  return current;
}

export function setLocale(next: LocaleCode) {
  current = next;
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Ignore.
  }
  if (typeof document !== 'undefined') document.documentElement.lang = next;
  for (const fn of subscribers) fn();
}

export function subscribeLocale(listener: () => void): () => void {
  subscribers.add(listener);
  return () => subscribers.delete(listener);
}

export function getDictionary(locale?: LocaleCode): Dictionary {
  return DICTIONARIES[locale ?? current];
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

/** Brazilian Real, always 2-decimal: `R$ 1.234,56`. */
export function formatBRL(amount: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(amount);
}

/**
 * Currency formatter that adapts to the active locale but keeps BRL as the
 * unit — this is a Brazilian product, the unit doesn't change with UI language.
 */
export function formatMoney(amount: number, locale: LocaleCode = current): string {
  return new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(amount);
}

// Wave 319 · BUG FIX (timezone): SQLite `datetime('now')` returns
// `'YYYY-MM-DD HH:MM:SS'` UTC sem marcador. Browsers parseiam como local
// time → conversão depois pra "America/Sao_Paulo" vira noop. Defensivo:
// se a string não tem timezone marker, assumimos UTC.
import { parseServerDate } from '@/lib/parse-server-date';

/** `dd/MM/yyyy` in São Paulo time. */
export function formatDate(iso: string, locale: LocaleCode = current): string {
  const d = parseServerDate(iso);
  if (!d) return '';
  return new Intl.DateTimeFormat(locale, {
    timeZone: PROJECT_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

/** `dd/MM/yyyy, HH:mm` 24-hour, São Paulo time. */
export function formatDateTime(iso: string, locale: LocaleCode = current): string {
  const d = parseServerDate(iso);
  if (!d) return '';
  return new Intl.DateTimeFormat(locale, {
    timeZone: PROJECT_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

/** `HH:mm` 24-hour only. */
export function formatTime(iso: string, locale: LocaleCode = current): string {
  const d = parseServerDate(iso);
  if (!d) return '';
  return new Intl.DateTimeFormat(locale, {
    timeZone: PROJECT_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

/**
 * "agora" / "há 5 min" / "há 2 h" / "há 3 dias" / fallback to formatDate.
 * Small vocabulary — no Intl.RelativeTimeFormat because we want exact
 * wording control and avoid its "in 2 minutes" edge cases for past-only.
 */
export function formatRelative(iso: string, locale: LocaleCode = current): string {
  const dict = DICTIONARIES[locale].time;
  const d = parseServerDate(iso);
  if (!d) return '';
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 60_000) return dict.justNow;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 60) return dict.minutesAgo(mins);
  const hours = Math.floor(mins / 60);
  if (hours < 24) return dict.hoursAgo(hours);
  const days = Math.floor(hours / 24);
  if (days < 7) return dict.daysAgo(days);
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return dict.weeksAgo(weeks);
  return formatDate(iso, locale);
}

/** Formats a plain number with pt-BR grouping: `1.234,56`. */
export function formatNumber(n: number, locale: LocaleCode = current): string {
  return new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'pt-BR').format(n);
}

export type { Dictionary, LocaleCode, LocaleDescriptor };
